/**
 * Who speaks in a transcript passage, where the platform shows one outside the recording's page:
 * the passage panel of the graph and a passage hit in the chat's search card.
 *
 * A passage stores voices as numbers and nothing else. Whose a voice is — a person of the
 * recording, the questioner, the interpreter — is said on the recording's page and resolved by
 * the server each time the passage is read, so a read hands a **legend** beside the passage: one
 * entry per named voice. A voice with no entry was named by nobody and stays a number.
 *
 * Pure: a legend in, a map by voice out. No router, no data.
 */
import type { TFunction } from 'i18next'

/** The two things a voice can be that are not a person of the recording. */
export type SpeakerRole = 'questioner' | 'interpreter'

/**
 * One named voice as a read sends it: the same entry in the graph's node read and in the
 * agents' tool results. A person carries `name` (null when enrolled without one), `tag` and
 * `node_id`; a role carries `role`. A search answer lists the voices of several recordings, so
 * there each entry names its recording by `document_id`.
 */
export interface PassageSpeakerDto {
  voice: number
  name?: string | null
  tag?: string | null
  node_id?: string | null
  role?: string | null
  document_id?: string | null
}

/** Who a voice is said to be. */
export type PassageSpeaker =
  | { kind: 'person'; name: string | null; tag: string | null }
  | { kind: 'role'; role: SpeakerRole }

/** The named voices of one recording, by voice number. */
export type PassageSpeakers = ReadonlyMap<number, PassageSpeaker>

const ROLES: readonly string[] = ['questioner', 'interpreter'] satisfies readonly SpeakerRole[]

const isRole = (value: unknown): value is SpeakerRole =>
  typeof value === 'string' && ROLES.includes(value)

/** A value as a non-empty string, else null. */
const word = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null

/** An entry of the legend as a speaker; null for one that names nobody. */
function speakerOf(entry: PassageSpeakerDto): PassageSpeaker | null {
  if (isRole(entry.role)) return { kind: 'role', role: entry.role }
  // Any other role word is not ours to show as a person.
  if (word(entry.role)) return null
  const name = word(entry.name)
  const tag = word(entry.tag)
  return name || tag || word(entry.node_id) ? { kind: 'person', name, tag } : null
}

const sameSpeaker = (a: PassageSpeaker, b: PassageSpeaker) =>
  JSON.stringify(a) === JSON.stringify(b)

const isEntry = (entry: unknown): entry is PassageSpeakerDto =>
  typeof entry === 'object' &&
  entry !== null &&
  Number.isInteger((entry as PassageSpeakerDto).voice) &&
  (entry as PassageSpeakerDto).voice > 0

/**
 * The named voices of one recording, from a read's legend. With `documentId` — a search hit's —
 * only the entries of that recording count, and an entry or a hit that names no recording names
 * nobody. A voice two different speakers claim is left a number: a wrong name on a statement is
 * worse than none.
 */
export function passageSpeakers(legend: unknown, documentId?: string | null): PassageSpeakers {
  const speakers = new Map<number, PassageSpeaker>()
  if (!Array.isArray(legend)) return speakers
  const scoped = documentId !== undefined
  if (scoped && !documentId) return speakers
  const claimedTwice = new Set<number>()
  for (const entry of legend) {
    if (!isEntry(entry) || (scoped && entry.document_id !== documentId)) continue
    const speaker = speakerOf(entry)
    if (!speaker) continue
    const known = speakers.get(entry.voice)
    if (known && !sameSpeaker(known, speaker)) claimedTwice.add(entry.voice)
    else speakers.set(entry.voice, speaker)
  }
  for (const voice of claimedTwice) speakers.delete(voice)
  return speakers
}

const SPEAKER = 'investigations.entityDetail.passage.speaker'

/** What a chip says of a speaker: the name, and beside it — a person's only — the tag in mono. */
export interface SpeakerWords {
  name: string
  tag: string | null
}

/**
 * A speaker in the words of the recording's page: a person's name and tag, the role word of a
 * role. A person enrolled without a name is *Unknown person*, told apart by the tag.
 */
export function speakerWords(speaker: PassageSpeaker, t: TFunction): SpeakerWords {
  if (speaker.kind === 'role') return { name: t(`${SPEAKER}.${speaker.role}`), tag: null }
  return { name: speaker.name ?? t(`${SPEAKER}.unknownPerson`), tag: speaker.tag }
}
